import { useState, useMemo } from "react"
import H1 from "~/ui-rtk/components/ui/typography/H1"
import { Button, DateRangePicker } from "~/ui-rtk/components/ui/controls"
import Select from "~/ui-rtk/components/ui/controls/Select"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { DATE_FORMAT, isValidDate, usePeriod } from "~/ui-rtk/hooks/period"
import useDate from "~/ui-rtk/hooks/date"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline"

import useConnect from "./connect"
import useJourneyNavigation from "./hooks/useJourneyNavigation"
import StepBarList from "./components/StepBarList"
import StepSelector from "./components/StepSelector"
import InfoBanner from "./components/InfoBanner"

import {
  CHANNEL_TAB_OPTIONS,
  SESSION_FILTER_OPTIONS,
  GRANULARITY_OPTIONS,
  CHANNEL_DISPLAY_NAMES,
} from "./constants"
import type {
  ChannelTab,
  SessionFilterOption,
  GranularityOption,
} from "./types"

const dayjs = useDate()

export default function JourneyAnalysisPage() {
  const [activeChannel, setActiveChannel] = useState<ChannelTab>("total")
  const [sessionFilter, setSessionFilter] = useState<SessionFilterOption>("all")
  const [granularity, setGranularity] = useState<GranularityOption>("all_pages")

  const { period: dateRange, setPeriod, setComparePeriod } = usePeriod()

  const navigation = useJourneyNavigation()

  const effectiveDateRange = useMemo((): [string, string] => {
    const start =
      dateRange?.[0] ?? dayjs().subtract(6, "month").format(DATE_FORMAT)
    const end = dateRange?.[1] ?? dayjs().format(DATE_FORMAT)
    return [start, end]
  }, [dateRange])

  const { stepsData } = useConnect(
    activeChannel,
    sessionFilter,
    granularity,
    effectiveDateRange,
    navigation.selectedNodes,
    navigation.visibleSteps,
  )

  // Current step data
  const currentStepData = stepsData[navigation.visibleSteps - 1]
  const currentNodes = currentStepData?.nodes ?? []
  const currentIsLoading = currentStepData?.isLoading ?? false

  // The selected node name for the current step (highlighted bar)
  const currentSelectedNode =
    navigation.selectedNodes[navigation.visibleSteps - 1]

  const handleNodeClick = (nodeName: string) => {
    navigation.selectNode(nodeName, navigation.visibleSteps - 1)
  }

  const handleChangePeriods = ({
    start,
    end,
    compareStart,
    compareEnd,
  }: {
    start: Date
    end: Date
    compareStart?: Date | null
    compareEnd?: Date | null
  }) => {
    setPeriod(dayjs(start).format(DATE_FORMAT), dayjs(end).format(DATE_FORMAT))
    if (compareStart && compareEnd) {
      setComparePeriod(
        dayjs(compareStart).format(DATE_FORMAT),
        dayjs(compareEnd).format(DATE_FORMAT),
      )
    } else {
      setComparePeriod()
    }
  }

  return (
    <div className="h-full px-4 py-2 bg-basic-black text-basic-white">
      <div className="px-4">
        <Breadcrumbs
          items={[
            {
              name: "Web Merchandising",
              slug: brandRoutes.WEB_MERCHANDISING.ROOT,
            },
            {
              name: "Path Exploration",
              slug: brandRoutes.WEB_ANALYTICS.JOURNEY_ANALYSIS,
            },
          ]}
        />
        <H1>Path Exploration</H1>

        {/* Channel Tabs + Filters row */}
        <div className="my-system-lg flex items-center justify-between flex-wrap gap-4">
          <div className="join">
            {CHANNEL_TAB_OPTIONS.map(({ id, label }) => (
              <Button
                variant={{ variant: "solid", color: "blue" }}
                key={id}
                title={label}
                className={cn(
                  "join-item border border-system-blue-600 h-system-2xl px-system-lg py-system-sm rounded-system-md",
                  { "bg-system-blue-800": activeChannel !== id },
                  { "bg-system-blue-600": activeChannel === id },
                )}
                onClick={() => {
                  setActiveChannel(id)
                  navigation.startOver()
                }}
              >
                <span
                  className={cn("font-[Lato]", {
                    "text-base text-system-gray-300 font-normal":
                      activeChannel !== id,
                    "text-white font-semibold": activeChannel === id,
                  })}
                >
                  {label}
                </span>
              </Button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Select
              className="min-w-[160px]"
              options={SESSION_FILTER_OPTIONS}
              value={sessionFilter}
              onChange={e => {
                setSessionFilter(e.target.value as SessionFilterOption)
                navigation.startOver()
              }}
            />
            <Select
              className="min-w-[160px]"
              options={GRANULARITY_OPTIONS}
              value={granularity}
              onChange={e => {
                setGranularity(e.target.value as GranularityOption)
                navigation.startOver()
              }}
            />
            {dateRange?.[0] &&
              dateRange?.[1] &&
              isValidDate(dateRange[0]) &&
              isValidDate(dateRange[1]) && (
                <DateRangePicker
                  start={dayjs(dateRange[0]).toDate()}
                  end={dayjs(dateRange[1]).toDate()}
                  comparisonEnabled={false}
                  onChange={handleChangePeriods}
                />
              )}
          </div>
        </div>
      </div>

      {/* Main Chart Section */}
      <div className="px-4">
        <div className="bg-system-blue-800 rounded-lg p-6">
          {/* Title + Nav buttons */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">
              {CHANNEL_DISPLAY_NAMES[activeChannel]} Traffic Exploration
            </h2>
            <div className="flex items-center gap-2">
              <button
                className={cn(
                  "p-2 rounded-md border border-system-blue-600 transition-colors",
                  navigation.canGoBack
                    ? "hover:bg-system-blue-700 text-white"
                    : "text-system-gray-500 cursor-not-allowed",
                )}
                onClick={navigation.goBack}
                disabled={!navigation.canGoBack}
                title="Back"
              >
                <ArrowLeftIcon className="w-4 h-4" />
              </button>
              <button
                className={cn(
                  "p-2 rounded-md border border-system-blue-600 transition-colors",
                  navigation.canGoForward
                    ? "hover:bg-system-blue-700 text-white"
                    : "text-system-gray-500 cursor-not-allowed",
                )}
                onClick={navigation.goForward}
                disabled={!navigation.canGoForward}
                title="Forward"
              >
                <ArrowRightIcon className="w-4 h-4" />
              </button>
              <button
                className="p-2 rounded-md border border-system-blue-600 hover:bg-system-blue-700 text-white transition-colors"
                onClick={navigation.startOver}
                title="Start Over"
              >
                <ArrowPathIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Step Breadcrumbs */}
          <div className="mb-4">
            <StepSelector
              selectedNodes={navigation.selectedNodes}
              visibleSteps={navigation.visibleSteps}
              onNavigateToStep={navigation.navigateToStep}
            />
          </div>

          {/* Step Title */}
          <h3 className="text-lg font-medium mb-3 text-system-gray-200">
            Step {navigation.visibleSteps}
          </h3>

          {/* Bar List */}
          <StepBarList
            nodes={currentNodes}
            isLoading={currentIsLoading}
            selectedNodeName={currentSelectedNode}
            onNodeClick={handleNodeClick}
          />

          {/* Info Banner */}
          {navigation.visibleSteps === 1 && (
            <InfoBanner message="Click on a page bar above to explore where traffic flows next. You can drill down up to 3 steps deep." />
          )}
        </div>
      </div>
    </div>
  )
}
